import React, { useState, useEffect, Fragment, useMemo } from 'react';
import { Dialog, DialogTitle, Transition, DialogBackdrop, DialogPanel, Listbox, ListboxButton, ListboxOptions, ListboxOption, Checkbox } from '@headlessui/react';
import { XMarkIcon, ClockIcon, CalendarDaysIcon, CheckIcon, ChevronUpDownIcon, BuildingOfficeIcon } from '@heroicons/react/24/outline';
import Select from 'react-select';
import axios from '../../axios';
import { selectStyles } from '../../Utils/selectStyles';
import Table from '../UI/Table';
import notify from '../../Utils/notify';

const NewCitaModal = ({ open, onClose, sucursalId, fecha, hora, slotDuracionMinutos = 30, onCreated }) => {
    const [sucursal, setSucursal] = useState('');
    const [form, setForm] = useState({
        nombre_paciente: '',
        telefono_paciente: '',
        email: '',
        doctor: '',
        observaciones: '',
        nombre_agendo: '',
    });
    const [loading, setLoading] = useState(false);
    const [productos, setProductos] = useState([]);
    const [selectedProducts, setSelectedProducts] = useState([]);
    const [pacienteEsAgendador, setPacienteEsAgendador] = useState(false);
    const [codigoPais, setCodigoPais] = useState('+52'); // México por defecto
    const [toleranciaSucursal, setToleranciaSucursal] = useState(0);

    // Códigos de país comunes
    const codigosPais = [
        { code: '+52', country: 'México' },
        { code: '+1', country: 'USA/Canadá' },
        { code: '+34', country: 'España' },
        { code: '+54', country: 'Argentina' },
        { code: '+56', country: 'Chile' },
        { code: '+57', country: 'Colombia' },
        { code: '+51', country: 'Perú' },
        { code: '+58', country: 'Venezuela' },
        { code: '+593', country: 'Ecuador' },
        { code: '+591', country: 'Bolivia' },
        { code: '+595', country: 'Paraguay' },
        { code: '+598', country: 'Uruguay' },
        { code: '+503', country: 'El Salvador' },
        { code: '+502', country: 'Guatemala' },
        { code: '+504', country: 'Honduras' },
        { code: '+505', country: 'Nicaragua' },
        { code: '+506', country: 'Costa Rica' },
        { code: '+507', country: 'Panamá' },
    ];

    // Cargar datos de la sucursal (para obtener tolerancia)
    useEffect(() => {
        if (open && sucursalId) {
            axios.get(`api/sucursals/${sucursalId}`).then((resp) => {
                setSucursal(resp.data.nombre_sucursal || '');
                setToleranciaSucursal(resp.data.tolerancia_minutos || 0);
            }).catch(e => {
                console.error('Error cargando sucursal:', e);
                setToleranciaSucursal(0);
            });
        }
    }, [open, sucursalId]);

    // Cargar productos al abrir el modal
    useEffect(() => {
        if (open) {
            axios.get('api/products').then((resp) => {
                setProductos(resp.data);
            }).catch(e => console.log("Error al cargar productos: ", e));
        }
    }, [open]);

    // Resetear formulario al abrir
    useEffect(() => {
        if (open) {
            setForm({
                nombre_paciente: '',
                telefono_paciente: '',
                email: '',
                doctor: '',
                observaciones: '',
                nombre_agendo: '',
            });
            setSelectedProducts([]);
            setPacienteEsAgendador(false);
            setCodigoPais('+52');
        }
    }, [open]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
    };

    const sendCreatedAppointmentNotifications = async (appointmentId) => {
        try {
            const resp = await axios.post(`api/appointments/${appointmentId}/resend-notifications`);

            if (resp.status === 200 && resp.data.success) {
                const emailSent = resp.data.notifications?.email?.sent || false;
                const whatsappSent = resp.data.notifications?.whatsapp?.sent || false;

                if (emailSent && whatsappSent) {
                    notify.success('Notificaciones enviadas por email y WhatsApp');
                } else if (emailSent) {
                    notify.success('Notificación enviada por email');
                } else if (whatsappSent) {
                    notify.success('Notificación enviada por WhatsApp');
                } else {
                    notify.warning('La cita se creó, pero no se pudo enviar ninguna notificación. Verifica email o teléfono.');
                }
            }
        } catch (error) {
            console.log('Error al enviar notificaciones automáticas:', error);
            const errorMsg = error.response?.data?.message || 'La cita se creó, pero hubo un error al enviar notificaciones';
            notify.warning(errorMsg);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!form.nombre_paciente.trim()) {
            notify.warning('El nombre del paciente es requerido');
            return;
        }
        if (!form.telefono_paciente.trim()) {
            notify.warning('El teléfono del paciente es requerido');
            return;
        }

        setLoading(true);
        try {
            // Validar que no sea día festivo
            const validacionFestivo = await axios.post('api/holidays/verificar', {
                fecha: fecha,
                sucursal_id: sucursalId
            });

            if (validacionFestivo.data.es_festivo) {
                const festivoNombre = validacionFestivo.data.festivo?.nombre || 'Día festivo';
                notify.error(`No se pueden agendar citas en días festivos: ${festivoNombre}`);
                setLoading(false);
                return;
            }

            // Concatenar código de país con teléfono
            const telefonoCompleto = `${codigoPais}${form.telefono_paciente}`.trim();

            // Calcular excedente de tiempo con tolerancia de sucursal
            const duracionTotal = selectedProducts.reduce((acc, p) => acc + (Number(p.duracion) || 0), 0);
            const tiempoDisponible = slotDuracionMinutos + toleranciaSucursal;
            const tiempoExcedido = duracionTotal > tiempoDisponible ? duracionTotal - tiempoDisponible : 0;
            console.log(`Duración total: ${duracionTotal} min, Tiempo disponible: ${tiempoDisponible} min, Excedido: ${tiempoExcedido} min`);
            const resp = await axios.post('api/appointments', {
                sucursal_id: sucursalId,
                fecha,
                hora,
                nombre_paciente: form.nombre_paciente.trim(),
                telefono_paciente: telefonoCompleto,
                email: form.email.trim() || null,
                doctor: form.doctor.trim() || null,
                observaciones: form.observaciones.trim() || null,
                nombre_agendo: form.nombre_agendo.trim() || null,
                products: selectedProducts.map(p => p.value),
                source: 'Backoffice',
                status: 'pendiente',
                excedido: tiempoExcedido,
            });
            notify.success('Cita creada correctamente');

            if (resp.data?.id) {
                await sendCreatedAppointmentNotifications(resp.data.id);
            }

            onCreated(resp.data);
            onClose();
        } catch (err) {
            const msg = err.response?.data?.message || err.response?.data?.error || 'Error al crear la cita';
            notify.error(msg);
        } finally {
            setLoading(false);
        }
    };

    // País seleccionado para el selector
    const paisSeleccionado = useMemo(() => {
        return codigosPais.find(p => p.code === codigoPais) || codigosPais[0];
    }, [codigoPais]);

    // Opciones para react-select de productos
    const productosOptions = useMemo(() => {
        return productos.map(p => ({
            value: p.id,
            label: `${p.nombre_producto} (${p.duracion_aproximada} min)`,
            duracion: p.duracion_aproximada
        }));
    }, [productos]);

    // Calcular duración total
    const totalDuracion = useMemo(() => {
        return selectedProducts.reduce((acc, p) => acc + (Number(p.duracion) || 0), 0);
    }, [selectedProducts]);

    // Productos completos seleccionados (objetos completos de productos)
    const selectedProductosCompletos = useMemo(() => {
        return selectedProducts.map(sp => {
            const producto = productos.find(p => p.id === sp.value);
            return producto;
        }).filter(Boolean);
    }, [selectedProducts, productos]);

    // Validar si la duración excede el tiempo del slot actual
    // slotDuracionMinutos viene calculado desde el componente padre (diferencia entre slots)
    const excedeTiempo = totalDuracion > slotDuracionMinutos;
    const excedente = excedeTiempo ? totalDuracion - slotDuracionMinutos : 0;

    return (
        <Transition appear show={open} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={()=>void 0}>
                <DialogBackdrop className="fixed inset-0 bg-black/40 backdrop-blur-sm" />
                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-200"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-150"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <DialogPanel className="w-full max-w-4xl bg-white rounded-lg shadow-xl mx-auto z-50 relative">
                                {/* Header */}
                                <div className="flex justify-between items-center border-b bg-linear-to-r from-blue-500 to-blue-600 px-6 py-4 rounded-t-lg">
                                    <DialogTitle className="text-lg font-semibold text-white m-2 flex items-center gap-2">
                                        <CalendarDaysIcon className="h-5 w-5" />
                                        Nueva Cita
                                    </DialogTitle>
                                    <button onClick={onClose} className="text-white hover:text-gray-200">
                                        <XMarkIcon className="h-6 w-6" />
                                    </button>
                                </div>

                                {/* Fecha / Hora / Sucursal */}
                                <div className="px-6 pt-4 pb-2 flex items-center gap-3 text-sm text-gray-600 bg-gray-50 border-b">
                                    <CalendarDaysIcon className="h-4 w-4 text-blue-500" />
                                    <span className="font-medium">{fecha}</span>
                                    <ClockIcon className="h-4 w-4 text-blue-500 ml-2" />
                                    <span className="font-medium">{hora}</span>
                                    <BuildingOfficeIcon className="h-4 w-4 text-blue-500" />
                                    <span className="font-medium">{sucursal}</span>
                                </div>

                                {/* Body */}
                                <form onSubmit={handleSubmit}>
                                    <div className="px-6 py-4 grid grid-cols-12 gap-4 max-h-[70vh] overflow-y-auto">
                                        {/* Nombre del paciente */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label htmlFor="nombre_paciente" className="block text-sm font-medium text-gray-700 mb-1">
                                                Nombre del paciente <span className="text-red-500">*</span>
                                            </label>
                                            <input
                                                id="nombre_paciente"
                                                type="text"
                                                name="nombre_paciente"
                                                value={form.nombre_paciente}
                                                onChange={handleChange}
                                                className="w-full dento-input"
                                                placeholder="Nombre completo del paciente"
                                                required
                                            />
                                        </div>

                                        {/* Email */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                                                Email <span className="text-xs text-gray-500">(opcional)</span>
                                            </label>
                                            <input
                                                id="email"
                                                type="email"
                                                name="email"
                                                value={form.email}
                                                onChange={handleChange}
                                                className="w-full dento-input"
                                                placeholder="correo@ejemplo.com"
                                            />
                                        </div>

                                        {/* Checkbox paciente es agendador */}
                                        <div className="col-span-12 md:col-span-6 flex justify-start items-center gap-4">
                                            <div className="flex justify-center gap-2 mb-1">
                                                <Checkbox
                                                    checked={pacienteEsAgendador}
                                                    onChange={(checked) => {
                                                        setPacienteEsAgendador(checked);
                                                        if (checked) {
                                                            setForm(prev => ({
                                                                ...prev,
                                                                nombre_agendo: prev.nombre_paciente || ''
                                                            }));
                                                        } else {
                                                            setForm(prev => ({
                                                                ...prev,
                                                                nombre_agendo: ''
                                                            }));
                                                        }
                                                    }}
                                                    className="h-4 w-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                                                    id="paciente-es-agendador"
                                                >
                                                    {({ checked }) => (
                                                        <span
                                                            className={`inline-block h-4 w-4 rounded border border-gray-300 mr-2 ${checked ? 'bg-blue-600' : 'bg-white'} flex items-center justify-center transition-colors`}
                                                        >
                                                            {checked && (
                                                                <svg className="h-3 w-3 text-white" fill="none" viewBox="0 0 20 20" stroke="currentColor">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l4 4L15 7" />
                                                                </svg>
                                                            )}
                                                        </span>
                                                    )}
                                                </Checkbox>
                                                <label htmlFor="paciente-es-agendador" className="text-sm text-gray-700 cursor-pointer select-none">
                                                    El paciente es quien agenda la cita
                                                </label>
                                            </div>
                                        </div>

                                        {/* Nombre del agendador */}
                                        {!pacienteEsAgendador && (
                                            <div className="col-span-12 md:col-span-6">
                                                <label htmlFor="nombre_agendo" className="block text-sm font-medium text-gray-700 mb-1">
                                                    Nombre del agendador
                                                </label>
                                                <input
                                                    id="nombre_agendo"
                                                    type="text"
                                                    name="nombre_agendo"
                                                    value={form.nombre_agendo}
                                                    onChange={handleChange}
                                                    className="w-full dento-input"
                                                    placeholder="Nombre de quien agenda la cita"
                                                />
                                            </div>
                                        )}

                                        {/* Teléfono con código de país */}
                                        <div className="col-span-12">
                                            <label htmlFor="telefono_paciente" className="block text-sm font-medium text-gray-700 mb-1">
                                                Teléfono | Celular <span className="text-red-500">*</span>
                                            </label>
                                            <div className="flex items-center gap-2">
                                                {/* Selector de código de país */}
                                                <Listbox value={paisSeleccionado} onChange={(pais) => setCodigoPais(pais.code)}>
                                                    <div className="relative w-40">
                                                        <ListboxButton className="relative w-full border-2 border-cyan-400 rounded-md px-3 py-2.5 text-left text-sm font-medium text-gray-700 bg-white cursor-pointer hover:border-blue-500 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 focus:outline-none shadow-sm">
                                                            <span className="block truncate">
                                                                {paisSeleccionado.code} {paisSeleccionado.country}
                                                            </span>
                                                            <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                                                <ChevronUpDownIcon className="h-5 w-5 text-gray-400" />
                                                            </span>
                                                        </ListboxButton>
                                                        <Transition
                                                            as={Fragment}
                                                            leave="transition ease-in duration-100"
                                                            leaveFrom="opacity-100"
                                                            leaveTo="opacity-0"
                                                        >
                                                            <ListboxOptions className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-sm shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                                                                {codigosPais.map((pais) => (
                                                                    <ListboxOption
                                                                        key={pais.code}
                                                                        value={pais}
                                                                        className={({ active }) =>
                                                                            `relative cursor-pointer select-none py-2 pl-7 pr-4 ${
                                                                                active ? 'bg-blue-100 text-blue-900' : 'text-gray-900'
                                                                            }`
                                                                        }
                                                                    >
                                                                        {({ selected }) => (
                                                                            <>
                                                                                <span className={`block truncate ${selected ? 'font-semibold' : 'font-normal'}`}>
                                                                                    {pais.code} - {pais.country}
                                                                                </span>
                                                                                {selected && (
                                                                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-blue-600">
                                                                                        <CheckIcon className="h-5 w-5" />
                                                                                    </span>
                                                                                )}
                                                                            </>
                                                                        )}
                                                                    </ListboxOption>
                                                                ))}
                                                            </ListboxOptions>
                                                        </Transition>
                                                    </div>
                                                </Listbox>
                                                {/* Input de teléfono */}
                                                <input
                                                    id="telefono_paciente"
                                                    type="tel"
                                                    name="telefono_paciente"
                                                    value={form.telefono_paciente}
                                                    onChange={(e) => {
                                                        const value = e.target.value.replace(/\D/g, '');
                                                        setForm(prev => ({ ...prev, telefono_paciente: value }));
                                                    }}
                                                    className="flex-1 dento-input"
                                                    placeholder="Número de teléfono"
                                                    required
                                                />
                                            </div>
                                        </div>

                                        {/* Estudios/Servicios */}
                                        <div className="col-span-12">
                                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                                Estudios/Servicios <span className="text-xs text-gray-500">(opcional)</span>
                                            </label>
                                            <Select
                                                isMulti
                                                options={productosOptions}
                                                value={selectedProducts}
                                                onChange={setSelectedProducts}
                                                placeholder="Seleccionar estudios..."
                                                noOptionsMessage={() => "No hay estudios disponibles"}
                                                styles={selectStyles}
                                                className="react-select-container"
                                                classNamePrefix="react-select"
                                            />
                                            {excedeTiempo && (
                                                <p className="mt-2 text-sm text-red-600 bg-red-50 rounded px-2 py-1 flex items-center gap-2">
                                                    <ClockIcon className="h-4 w-4" />
                                                    La duración de los estudios se excede por {excedente} minutos (Slot: {slotDuracionMinutos} min, Total: {totalDuracion} min)
                                                </p>
                                            )}
                                        </div>

                                        {/* Vista previa de servicios seleccionados */}
                                        {selectedProductosCompletos.length > 0 && (
                                            <div className="col-span-12">
                                                <div className="bg-linear-to-r from-blue-50 to-indigo-50 rounded-lg p-4 border border-blue-100">
                                                    <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                                                        <ClockIcon className="h-5 w-5 text-blue-500" />
                                                        Resumen de Servicios
                                                    </h4>
                                                    <Table
                                                        columns={[
                                                            { 
                                                                key: 'nombre_producto', 
                                                                label: 'Servicio',
                                                                render: (value) => (
                                                                    <span className="font-medium text-gray-800">{value}</span>
                                                                )
                                                            },
                                                            { 
                                                                key: 'duracion_aproximada', 
                                                                label: 'Duración',
                                                                align: 'right',
                                                                render: (value) => (
                                                                    <span className="inline-flex items-center gap-1 text-gray-600">
                                                                        <ClockIcon className="h-4 w-4" />
                                                                        {value} min
                                                                    </span>
                                                                )
                                                            }
                                                        ]}
                                                        data={selectedProductosCompletos}
                                                        keyExtractor={(row) => row.id}
                                                        striped={true}
                                                        hoverable={true}
                                                        shadow={false}
                                                        bordered={false}
                                                        rounded={true}
                                                        responsive={false}
                                                        emptyMessage="No hay servicios seleccionados"
                                                    />
                                                    {/* Total de duración */}
                                                    <div className="mt-3 pt-3 border-t-2 border-blue-200 flex justify-between items-center bg-blue-100/50 rounded-b-lg px-4 py-2">
                                                        <span className="font-bold text-gray-800">Total</span>
                                                        <span className="font-bold text-blue-700 flex items-center gap-1">
                                                            <ClockIcon className="h-4 w-4" />
                                                            {totalDuracion} min
                                                            {totalDuracion >= 60 && (
                                                                <span className="text-xs text-gray-500 ml-1">
                                                                    ({Math.floor(totalDuracion / 60)}h {totalDuracion % 60}m)
                                                                </span>
                                                            )}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}

                                        {/* Doctor */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label htmlFor="doctor" className="block text-sm font-medium text-gray-700 mb-1">
                                                Doctor
                                            </label>
                                            <input
                                                id="doctor"
                                                type="text"
                                                name="doctor"
                                                value={form.doctor}
                                                onChange={handleChange}
                                                className="w-full dento-input"
                                                placeholder="Ej. Dr. García"
                                            />
                                        </div>

                                        {/* Observaciones */}
                                        <div className="col-span-12 md:col-span-6">
                                            <label htmlFor="observaciones" className="block text-sm font-medium text-gray-700 mb-1">
                                                Observaciones
                                            </label>
                                            <textarea
                                                id="observaciones"
                                                name="observaciones"
                                                value={form.observaciones}
                                                onChange={handleChange}
                                                rows={3}
                                                className="w-full border-2 border-cyan-400 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                                                placeholder="Notas adicionales..."
                                            />
                                        </div>
                                    </div>

                                    {/* Footer */}
                                    <div className="bg-gray-50 px-6 py-4 rounded-b-lg flex justify-end gap-3">
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                                            disabled={loading}
                                        >
                                            Cancelar
                                        </button>
                                        <button
                                            type="submit"
                                            disabled={loading}
                                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                                        >
                                            {loading ? 'Creando...' : 'Crear Cita'}
                                        </button>
                                    </div>
                                </form>
                            </DialogPanel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    );
};

export default NewCitaModal;
